import { useCurrentUserLocale } from "@app/shared/utils/currentUserLocale";
import { SupportedLocales } from "@app/shared/utils/locales";
import { nameNormalized } from "@app/shared/utils/tableSortingFns";
import { ProgressBar } from "@design-system/DataDisplay/ProgressBar/ProgressBar";
import { StatusTagCircular } from "@design-system/DataDisplay/StatusTagCircular";
import { FlexRow } from "@design-system/Layout/Flex";
import { compareBasic, type ColumnDef } from "@design-system/Table";
import { Text } from "@design-system/Typography/Text";
import {
    FrameworkProject,
    FrameworkProjectStatus,
} from "@generated/client/graphql";
import dayjs from "dayjs";
import { useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import { AssigneesUserAvatar } from "../../../components/AssigneesUserAvatar";
import { useFrameworkTranslations } from "../../i18n";

export const columns: ColumnDef<FrameworkProject>[] = [
    {
        id: "name",
        accessorKey: "name",
        meta: {
            headerClassName: "w-[336px] min-w-[336px] max-w-[336px]",
            cellClassName: "w-[336px] min-w-[336px] max-w-[336px]",
            colWidth: "336px",
        },
        sortingFn: nameNormalized,
        header: () => {
            const { t, keys } = useFrameworkTranslations();
            return (
                <Text variant="body-sm-bold" className="text-secondary">
                    {t(keys.table_header_name)}
                </Text>
            );
        },
        cell: ({ row }) => {
            const { org_uname, framework_group_id } = useParams();
            return (
                <Link
                    className="[all:unset]"
                    to={`/o/${org_uname}/admin/frameworks/${framework_group_id}/${row.original.id}`}
                >
                    <Text variant="body-bold" className="cursor-pointer">
                        {row.original.name}
                    </Text>
                </Link>
            );
        },
    },

    {
        id: "status",
        accessorKey: "status",
        meta: {
            headerClassName: "w-[107px] min-w-[107px] max-w-[107px]",
            cellClassName: "w-[107px] min-w-[107px] max-w-[107px]",
            colWidth: "107px",
        },
        sortingFn: (rowA, rowB) => {
            const statusOrder = {
                NOT_STARTED: 0,
                IN_PROGRESS: 1,
                VALIDATED: 2,
            };
            const statusA = rowA.original.status;
            const statusB = rowB.original.status;
            return statusOrder[statusA] - statusOrder[statusB];
        },
        header: () => {
            const { t, keys } = useFrameworkTranslations();
            return (
                <Text variant="body-sm-bold" className="text-secondary">
                    {t(keys.table_header_status)}
                </Text>
            );
        },
        cell: ({ row }) => {
            const status = row.original.status;

            if (status === FrameworkProjectStatus.Validated) {
                return <StatusTagCircular status="validated" size="sm" />;
            } else if (status === FrameworkProjectStatus.InProgress) {
                return <StatusTagCircular status="inProgress" size="sm" />;
            } else if (status === FrameworkProjectStatus.NotStarted) {
                return <StatusTagCircular status="todo" size="sm" />;
            }
        },
    },
    {
        id: "progress",
        accessorKey: "progress",
        meta: {
            cellClassName: "min-w-[200px]",
            headerClassName: "min-w-[200px]",
            colWidth: "200px",
        },
        sortingFn: (rowA, rowB) => {
            const { validatedQuestions, totalQuestions } = rowA.original;
            const {
                validatedQuestions: validatedQuestionsB,
                totalQuestions: totalQuestionsB,
            } = rowB.original;
            return compareBasic(
                validatedQuestions / totalQuestions,
                validatedQuestionsB / totalQuestionsB,
            );
        },
        header: () => {
            const { t, keys } = useFrameworkTranslations();
            return (
                <Text variant="body-sm-bold" className="text-secondary">
                    {t(keys.table_header_progress)}
                </Text>
            );
        },
        cell: ({ row }) => {
            const { t, keys } = useFrameworkTranslations();
            const { validatedQuestions, totalQuestions } = row.original;

            if (totalQuestions === 0) {
                return "-";
            }
            return (
                <FlexRow gap="5" w="full">
                    <ProgressBar
                        segments={[
                            {
                                value: validatedQuestions,
                                labelValue: `${(((validatedQuestions || 0) / (totalQuestions || 0)) * 100).toFixed(0)}%`,
                                label: t(keys.table_header_progress),
                            },
                        ]}
                        total={totalQuestions}
                        noDataPlaceholder="N/A"
                    />
                    <Text>{`${validatedQuestions} / ${totalQuestions}`}</Text>
                </FlexRow>
            );
        },
    },

    {
        id: "assignees",
        meta: {
            cellClassName: "min-w-[120px]",
            headerClassName: "min-w-[120px]",
        },
        header: () => {
            const { t, keys } = useFrameworkTranslations();
            return (
                <Text variant="body-sm-bold" className="text-secondary">
                    {t(keys.table_header_assignees)}
                </Text>
            );
        },
        cell: ({ row }) => {
            const assignees = row.original.assignees;

            return assignees.length ? (
                <AssigneesUserAvatar assignees={assignees} />
            ) : (
                "-"
            );
        },
    },
    {
        id: "updatedAt",
        accessorKey: "updatedAt",
        meta: {
            cellClassName: "min-w-[140px]",
            headerClassName: "min-w-[140px]",
        },
        header: () => {
            const { t, keys } = useFrameworkTranslations();
            return (
                <Text variant="body-sm-bold" className="text-secondary">
                    {t(keys.table_header_last_modified)}
                </Text>
            );
        },
        cell: ({ row }) => {
            const userLocale = useCurrentUserLocale(SupportedLocales.EN);

            const lastModifiedString = useMemo(() => {
                if (!row.original.updatedAt) {
                    return "-";
                }

                // if it is today, return the hour and minute
                if (dayjs(row.original.updatedAt).isSame(dayjs(), "day")) {
                    return dayjs(row.original.updatedAt).format("HH:mm");
                }

                return dayjs(row.original.updatedAt).format(
                    userLocale === "fr" ? "DD MMMM, YYYY" : "MMM DD, YYYY",
                );
            }, [row.original.updatedAt, userLocale]);

            return lastModifiedString;
        },
    },
];
